import { deleteIcon, filterIcon, getTechnologyIconName, pencilIcon } from '../../../../styles';
import {
  ICONS,
  VPPA_RFQ_STATUS,
  ProfileVisibility,
  CONTRACT_TYPE,
  METHOD_EXECUTION,
  PROFILE_RIGHTS,
} from '../../../../utils';
import { CUSTOM_HEADER_TOOLTIPS, headerWithTooltipColParams } from '../../../../utils/grid';
import {
  ActionCellRenderer,
  ActionTypes,
} from '../../../../utils/custom-renderer/action-cell-renderer';
import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../utils/custom-renderer/hyperlink-cell-renderer';
import {
  getNumericPattern,
  getStatusTranslate,
  getFormattedNumber,
  getNumericDecimalPattern,
  getStatusColour,
  formatMMDDYYYY,
  getFillColorFromProjectStatus,
} from '../../../../utils/formatting';
import { getAbbreviationForCreditProfile } from '../../../../utils/credit-profiles';
import { CASCADE_ENUM } from '../../../../services/enum.service';
import type { MarketDepthGrid } from './market-depth-grid';
import { TechnologyIconCellRenderer } from '../../../../utils/custom-renderer/technology-icon-cell-renderer';
import { CreditProfileLegendTooltipComponent } from '../../../../utils/custom-renderer/credit-profile-legend-tooltip-component';

export enum COLUMN_FIELDS {
  RFQ_UNIQUE_ID = 'RFQ_UNIQUE_ID',
  RFQ_STATUS = 'RFQ_STATUS',
  RFQ_CREDIT_PROFILE = 'BUYER_POST_COD_COLLATERAL',
  RFQ_VOLUME = 'RFQ_VOLUME',
  RFQ_CAPACITY = 'RFQ_CAPACITY',
  RFQ_SETTLEMENT_POINT = 'RFQ_SETTLEMENT_POINT',
  RFQ_ISO = 'RFQ_ISO',
  RFQ_TENOR_START_DATE = 'RFQ_TENOR_START_DATE',
  RFQ_TENOR_MONTHS = 'RFQ_TENOR_MONTHS',
  RFQ_APPLICABLE_REC_PROGRAM = 'RFQ_APPLICABLE_REC_PROGRAM',
  RFQ_TECHNOLOGY = 'RFQ_TECHNOLOGY',
  FIXED_PRICE = 'FIXED_PRICE',
  VPPA_EXPECTED_SETTLEMENT = 'VPPA_EXPECTED_SETTLEMENT',
  VPPA_IMPLIED_CARBON_PRICE = 'VPPA_IMPLIED_CARBON_PRICE',
  VPPA_ISO = 'VPPA_ISO',
  VPPA_SETTLEMENT_POINT = 'VPPA_SETTLEMENT_POINT',
  VPPA_NAME = 'VPPA_NAME',
  VPPA_TENOR_START_DATE = 'VPPA_TENOR_START_DATE',
  VPPA_OFFERED_CAPACITY = 'VPPA_OFFERED_CAPACITY',
  VPPA_APPLICABLE_REC_PROGRAM = 'VPPA_APPLICABLE_REC_PROGRAM',
  VPPA_CREDIT_PROFILE = 'SELLER_POST_COD_COLLATERAL',
  VPPA_STATUS = 'VPPA_STATUS',
  VPPA_UNIQUE_ID = 'VPPA_UNIQUE_ID',
  PROPOSAL_ACTION = 'PROPOSAL_ACTION',
  VPPA_PROPOSAL_ACTION = 'VPPA_PROPOSAL_ACTION',
  VPPA_TENOR_MONTHS = 'VPPA_TENOR_MONTHS',
}

function isCompanyValidForAutoExecuted(params: any, context: MarketDepthGrid): boolean {
  if (
    context.methodOfExecution === METHOD_EXECUTION.MINI_POST_BUILD_VPPA_AUTO_EXECUTED &&
    params.data.RFQ_COMPANY_ID !== context.actualCompanyId &&
    params.data.VPPA_COMPANY_ID !== context.actualCompanyId
  ) {
    const buyerId: string = !params.data.RFQ_COMPANY_ID
      ? context.actualCompanyId
      : params.data.RFQ_COMPANY_ID;
    const sellerId: string = !params.data.VPPA_COMPANY_ID
      ? context.actualCompanyId
      : params.data.VPPA_COMPANY_ID;
    const buyerCounterParties = context.companyApprovalsMap.get(buyerId);
    const sellerCounterParties = context.companyApprovalsMap.get(sellerId);

    if (buyerId && sellerId && buyerCounterParties && sellerCounterParties) {
      if (
        buyerCounterParties.length > 0 &&
        !buyerCounterParties.find((x) => x.CASCADE_COMPANY_ID === sellerId) &&
        sellerCounterParties.length > 0 &&
        !sellerCounterParties.find((x) => x.CASCADE_COMPANY_ID === buyerId)
      ) {
        return false;
      }
    } else {
      return false;
    }
  }
  return true;
}

function getStatusStyle(params: any, context: MarketDepthGrid) {
  const response = {};
  if (params.data?.RFQ_ID && params.data.RFQ_COMPANY_ID === context.actualCompanyId) {
    response['background-color'] = '#344232';
  }

  if (params.data?.RFQ_STATUS) {
    if (!isCompanyValidForAutoExecuted(params, context)) {
      response['opacity'] = '0.55';
    }
    response['color'] = getStatusColour(params.data.RFQ_STATUS);
  }

  if (!isCompanyValidForAutoExecuted(params, context)) {
    response['color'] = 'rgba(201, 201, 201)';
    response['opacity'] = '0.55';
  }
  return response;
}

function getOwnCompanyColor(params: any, context: MarketDepthGrid): any {
  if (params.data?.VPPA_ID && params.data.VPPA_COMPANY_ID === context.actualCompanyId) {
    return { 'background-color': '#344232' };
  }
  if (!isCompanyValidForAutoExecuted(params, context)) {
    return { color: 'rgba(201, 201, 201, 0.55)' };
  }
  if (params.data?.VPPA_UNIQUE_ID === context.marketDepthService.selectedVppaId) {
    return { 'background-color': '#344232' };
  }
  return '';
}

function getStatusOwnCompanyStyle(params: any, context) {
  const response = {};
  if (!isCompanyValidForAutoExecuted(params, context)) {
    response['color'] = 'rgba(201, 201, 201, 0.55)';
  }

  if (params.data?.VPPA_ID && params.data.VPPA_COMPANY_ID === context.actualCompanyId) {
    response['background-color'] = '#344232';
  }

  if (params.data?.VPPA_STATUS === VPPA_RFQ_STATUS.ACTIVE) {
    response['color'] = '#7acc79';
  }

  if (params.data?.VPPA_STATUS === VPPA_RFQ_STATUS.EXISTING_INTEREST) {
    response['color'] = 'rgba(255, 182, 96, 0.99)';
  }

  if (params.data?.VPPA_UNIQUE_ID === context.marketDepthService.selectedVppaId) {
    return { 'background-color': '#344232' };
  }

  return response;
}

function getRfqtextColor(params: any, context: MarketDepthGrid): any {
  const response = {};

  if (params.data?.RFQ_ID && params.data.RFQ_COMPANY_ID === context.actualCompanyId) {
    response['background-color'] = '#344232';
  }

  if (!isCompanyValidForAutoExecuted(params, context)) {
    response['color'] = 'rgba(201, 201, 201, 0.55)';
  }
  return response;
}

export function MARKET_DEPTH_COLUMNS(context: MarketDepthGrid) {
  return [
    {
      headerName: 'Bids',
      headerClass: 'ag-header-blue',
      marryChildren: true,
      children: [
        {
          field: COLUMN_FIELDS.VPPA_PROPOSAL_ACTION,
          headerName: '',
          suppressMenu: true,
          maxWidth: 140,
          suppressRowClickSelection: true,
          suppressColumnsToolPanel: true,
          lockPosition: 'left',
          cellStyle: {
            'padding-right': '7px',
            'box-shadow': '-5px 0px 15px 0px rgba(0, 0, 0, 0.2)',
          },
          cellRenderer: ActionCellRenderer,
          cellRendererParams: (params) => {
            if (!params.data || params.data.VPPA_ID) {
              return;
            }

            let actions;

            if (
              params.data.RFQ_CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED
            ) {
              const hasPermission = context.userService
                .getFilteredPermissions()
                .includes(PROFILE_RIGHTS.AUTO_EXECUTE);

              if (context.actualCompanyId === params.data.RFQ_COMPANY_ID && hasPermission) {
                const filterBtn = {
                  name: context.isFiltering
                    ? filterIcon('content', '#a9ff78')
                    : filterIcon('content'),
                  callback: (detail) => {
                    if (detail) {
                      context.filterByAutoExecuted(detail);
                    }
                  },
                  type: ActionTypes.FILTER,
                  actionName: 'filter-rfq',
                  tooltip: 'Filter RFQ',
                };
                const editBtn = {
                  name: pencilIcon(),
                  callback: (detail) => {
                    if (detail) {
                      context.$emit('editRfq', detail);
                    }
                  },
                  type: ActionTypes.EDIT,
                  actionName: 'edit-rfq',
                  tooltip: 'Edit RFQ',
                };
                const deleteBtn = {
                  name: deleteIcon(),
                  callback: (detail) => {
                    if (detail) {
                      context.$emit('deleteRfq', detail);
                    }
                  },
                  type: ActionTypes.DELETE,
                  actionName: 'delete-rfq',
                  tooltip: 'Delete RFQ',
                };

                actions = [filterBtn, editBtn, deleteBtn];
              }
            } else if (
              context.actualCompanyId !== params.data.RFQ_COMPANY_ID &&
              context.authService.validateProfile(PROFILE_RIGHTS.UPSERT_NEGOTIATION)
            ) {
              const documentBtn = {
                name: 'Propose Contract',
                callback: (detail) => {
                  context.$emit('createVppaProposal', detail);
                },
                type: ActionTypes.CREATE,
                actionName: 'propose-vppa',
              };
              actions = [documentBtn];
            }

            return {
              actions,
              enabled: true,
            };
          },
        },
        {
          field: COLUMN_FIELDS.RFQ_UNIQUE_ID,
          headerName: 'Unique ID',
          suppressMenu: true,
          flex: 1,
          cellStyle: (params) => getRfqtextColor(params, context),
          sortable: false,
        },
        {
          field: COLUMN_FIELDS.RFQ_STATUS,
          headerName: 'Status',
          suppressMenu: true,
          flex: 1,
          sortable: false,
          cellStyle: (params) => getStatusStyle(params, context),
          valueFormatter: (params) => getStatusTranslate(params.data.RFQ_STATUS),
        },
        {
          field: COLUMN_FIELDS.RFQ_CREDIT_PROFILE,
          headerName: 'Credit Profile ($/MW)',
          suppressMenu: true,
          flex: 1,
          cellStyle: (params) => getRfqtextColor(params, context),
          sortable: false,
          valueFormatter: (params) => {
            if (params.data.RFQ_ID) {
              return getFormattedNumber(
                params.data.BUYER_POST_COD_COLLATERAL,
                getAbbreviationForCreditProfile(params.data.RFQ_CREDIT_PROFILE),
                2,
                '$',
              );
            }
          },
          ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
        },
        {
          field: COLUMN_FIELDS.RFQ_VOLUME,
          headerName: 'Volume (MWh/year)',
          suppressMenu: true,
          flex: 1,
          cellStyle: (params) => getRfqtextColor(params, context),
          sortable: false,
          valueFormatter: (params) => getFormattedNumber(params.data.RFQ_VOLUME),
        },
        {
          field: COLUMN_FIELDS.RFQ_APPLICABLE_REC_PROGRAM,
          headerName: 'REC Program',
          suppressMenu: true,
          flex: 1,
          cellStyle: (params) => getRfqtextColor(params, context),
          sortable: false,
          valueFormatter: (params) => {
            if (params.data) {
              return context.enumService.getEnumTranslation(
                CASCADE_ENUM.RFQ_APPLICABLE_REC_PROGRAM,
                params.data.RFQ_APPLICABLE_REC_PROGRAM,
              );
            }
          },
        },
        {
          field: COLUMN_FIELDS.RFQ_TENOR_START_DATE,
          headerName: 'Start Date',
          valueFormatter: (params) => {
            return formatMMDDYYYY(params.data.RFQ_TENOR_START_DATE);
          },
          suppressMenu: true,
          flex: 1,
          cellStyle: (params) => getRfqtextColor(params, context),
          sortable: false,
        },
        {
          field: COLUMN_FIELDS.RFQ_TENOR_MONTHS,
          headerName: 'Tenor (mo)',
          suppressMenu: true,
          flex: 1,
          cellStyle: (params) => getRfqtextColor(params, context),
          sortable: false,
        },
        {
          field: COLUMN_FIELDS.RFQ_TECHNOLOGY,
          headerName: 'Type',
          suppressMenu: true,
          flex: 1,
          cellStyle: (params) => getRfqtextColor(params, context),
          cellRenderer: TechnologyIconCellRenderer,
          cellRendererParams: (params) => params,
          sortable: false,
          ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.ICON_LEGEND),
        },
        {
          field: COLUMN_FIELDS.RFQ_SETTLEMENT_POINT,
          headerName: 'Settlement Point',
          suppressMenu: true,
          flex: 1,
          cellStyle: (params) => getRfqtextColor(params, context),
          sortable: false,
        },
        {
          field: COLUMN_FIELDS.RFQ_ISO,
          headerName: 'ISO',
          suppressMenu: true,
          flex: 1,
          cellStyle: (params) => getRfqtextColor(params, context),
          sortable: false,
        },
      ],
    },
    {
      headerName: '',
      headerClass: 'ag-header-gray',
      suppressColumnsToolPanel: true,
      children: [
        {
          field: COLUMN_FIELDS.FIXED_PRICE,
          headerName: 'Price',
          suppressMenu: true,
          suppressMovable: true,
          suppressColumnsToolPanel: true,
          flex: 1,
          sortable: true,
          valueFormatter: (params) => getNumericDecimalPattern(params.data.FIXED_PRICE, null, '$'),
          headerClass: 'center-header',
          cellStyle: function (params) {
            const response = {};
            response['border-left'] = '1px solid rgba(255, 255, 255, 0.07)';
            response['border-right'] = '1px solid rgba(255, 255, 255, 0.07)';
            response['justify-content'] = 'center';
            if (params.data?.RFQ_ID) {
              if (!isCompanyValidForAutoExecuted(params, context)) {
                response['color'] = 'rgba(126, 170, 255, 0.55)';
              } else {
                response['color'] = 'rgba(126, 170, 255, 1)';
              }
            }
            if (params.data?.VPPA_ID) {
              if (!isCompanyValidForAutoExecuted(params, context)) {
                response['color'] = 'rgba(249, 100, 77, 0.55)';
              } else {
                response['color'] = 'rgba(249, 100, 77, 1)';
              }
            }
            return response;
          },
        },
      ],
    },
    {
      headerName: 'Offers',
      headerClass: 'ag-header-red',
      marryChildren: true,
      children: [
        {
          field: COLUMN_FIELDS.VPPA_EXPECTED_SETTLEMENT,
          headerName: 'Expected Settlement ($/MWh)',
          suppressMenu: true,
          flex: 1,
          sortable: true,
          valueFormatter: (params) =>
            getFormattedNumber(params.data.VPPA_EXPECTED_SETTLEMENT, '', 2, '$'),
          cellStyle: (params) => getOwnCompanyColor(params, context),
        },
        {
          field: COLUMN_FIELDS.VPPA_IMPLIED_CARBON_PRICE,
          headerName: 'Implied Carbon Price ($/MTCO₂)',
          suppressMenu: true,
          flex: 1,
          sortable: true,
          valueFormatter: (params) =>
            getFormattedNumber(params.data.VPPA_IMPLIED_CARBON_PRICE, '', 2, '$'),
          cellStyle: (params) => getOwnCompanyColor(params, context),
        },
        {
          field: COLUMN_FIELDS.VPPA_ISO,
          headerName: 'ISO',
          suppressMenu: true,
          flex: 1,
          sortable: false,
          cellStyle: (params) => getOwnCompanyColor(params, context),
        },
        {
          field: COLUMN_FIELDS.VPPA_SETTLEMENT_POINT,
          headerName: 'Settlement Point',
          suppressMenu: true,
          flex: 1,
          sortable: false,
          cellStyle: (params) => getOwnCompanyColor(params, context),
        },
        {
          field: COLUMN_FIELDS.VPPA_NAME,
          headerName: 'Project',
          suppressMenu: true,
          flex: 1,
          sortable: false,
          cellStyle: (params) => getOwnCompanyColor(params, context),
          valueGetter: (props) => {
            const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];
            iconList.push({
              icon: getTechnologyIconName(props.data.VPPA_TECHNOLOGY),
              width: '13px',
              height: '16px',
              fill: getFillColorFromProjectStatus(props.data.PROJECT_OPERATIONAL_STATUS),
            });

            if (
              props.data?.VPPA_ID &&
              props.data.VPPA_COMPANY_ID !== context.actualCompanyId &&
              context.permission === ProfileVisibility.SELLER
            ) {
              return JSON.stringify({
                NAME: '--',
                PROJECT_ID: '',
                ICON_LIST: iconList,
              });
            }

            return JSON.stringify({
              NAME: props.data.VPPA_NAME,
              PROJECT_ID: props.data.VPPA_PROJECT_ID,
              ICON_LIST: iconList,
            });
          },
          cellRenderer: HyperlinkStaticCellRenderer,
          cellRendererParams: (params) => {
            const obj = JSON.parse(params.value);
            if (!obj.NAME) {
              const hyperlinkParams: IGridHyperlink = {
                value: null,
                id: null,
                vppaId: null,
                iconList: [],
              };
              return hyperlinkParams;
            }

            const hyperlinkParams: IGridHyperlink = {
              value: obj.NAME,
              id: obj.PROJECT_ID,
              vppaId: null,
              iconList: obj.ICON_LIST,
            };
            return hyperlinkParams;
          },
        },
        {
          field: COLUMN_FIELDS.VPPA_TENOR_START_DATE,
          headerName: 'Start Date',
          valueFormatter: (params) => {
            return formatMMDDYYYY(params.data.VPPA_TENOR_START_DATE);
          },
          suppressMenu: true,
          flex: 1,
          sortable: false,
          cellStyle: (params) => getOwnCompanyColor(params, context),
        },
        {
          field: COLUMN_FIELDS.VPPA_TENOR_MONTHS,
          headerName: 'Tenor (mo)',
          suppressMenu: true,
          flex: 1,
          sortable: false,
          cellStyle: (params) => getOwnCompanyColor(params, context),
        },
        {
          field: COLUMN_FIELDS.VPPA_OFFERED_CAPACITY,
          headerName: 'Offered Capacity (MW)',
          suppressMenu: true,
          flex: 1,
          sortable: false,
          valueFormatter: (params) => getNumericPattern(params.data.VPPA_OFFERED_CAPACITY),
          cellStyle: (params) => getOwnCompanyColor(params, context),
        },
        {
          field: COLUMN_FIELDS.VPPA_APPLICABLE_REC_PROGRAM,
          headerName: 'REC Program',
          suppressMenu: true,
          flex: 1,
          sortable: false,
          valueFormatter: (params) => {
            if (params.data) {
              return context.enumService.getEnumTranslation(
                CASCADE_ENUM.APPLICABLE_REC_PROGRAM,
                params.data.VPPA_APPLICABLE_REC_PROGRAM,
              );
            }
          },
          cellStyle: (params) => getOwnCompanyColor(params, context),
        },
        {
          field: COLUMN_FIELDS.VPPA_CREDIT_PROFILE,
          headerName: 'Credit Profile ($/MW)',
          suppressMenu: true,
          flex: 1,
          sortable: false,
          valueFormatter: (params) => {
            if (params.data.VPPA_ID) {
              return getFormattedNumber(
                params.data.SELLER_POST_COD_COLLATERAL,
                getAbbreviationForCreditProfile(params.data.VPPA_CREDIT_PROFILE),
                2,
                '$',
              );
            }
          },
          cellStyle: (params) => getOwnCompanyColor(params, context),
          ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
        },
        {
          field: COLUMN_FIELDS.VPPA_STATUS,
          headerName: 'Status',
          suppressMenu: true,
          flex: 1,
          sortable: false,
          cellStyle: (params) => getStatusOwnCompanyStyle(params, context),
          valueFormatter: (params) => getStatusTranslate(params.data.VPPA_STATUS),
        },
        {
          field: COLUMN_FIELDS.VPPA_UNIQUE_ID,
          headerName: 'Unique ID',
          suppressMenu: true,
          flex: 1,
          sortable: false,
          cellStyle: (params) => getOwnCompanyColor(params, context),
          valueFormatter: (params) => {
            if (
              params.data?.VPPA_ID &&
              params.data.VPPA_COMPANY_ID !== context.actualCompanyId &&
              context.permission === ProfileVisibility.SELLER
            ) {
              return '--';
            }
            return params.data.VPPA_UNIQUE_ID;
          },
        },
        {
          field: COLUMN_FIELDS.PROPOSAL_ACTION,
          headerName: '',
          suppressMenu: true,
          maxWidth: 140,
          lockPosition: 'right',
          suppressRowClickSelection: true,
          suppressColumnsToolPanel: true,
          cellStyle: {
            'padding-right': '7px',
            'box-shadow': '-5px 0px 15px 0px rgba(0, 0, 0, 0.2)',
          },
          cellRenderer: ActionCellRenderer,
          cellRendererParams: (params) => {
            if (!params.data || params.data.RFQ_ID) {
              return;
            }

            let actions;
            if (
              params.data.VPPA_CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED
            ) {
              const hasPermission = context.userService
                .getFilteredPermissions()
                .includes(PROFILE_RIGHTS.AUTO_EXECUTE);
              if (context.actualCompanyId === params.data.VPPA_COMPANY_ID && hasPermission) {
                const filterBtn = {
                  name: context.isFiltering
                    ? filterIcon('content', '#a9ff78')
                    : filterIcon('content'),
                  callback: (detail) => {
                    if (detail) {
                      context.filterByAutoExecuted(detail);
                    }
                  },
                  type: ActionTypes.FILTER,
                  actionName: 'filter-vppa',
                  tooltip: 'Filter VPPA',
                };
                const editBtn = {
                  name: pencilIcon(),
                  callback: (detail) => {
                    if (detail) {
                      context.$emit('editVppa', detail);
                    }
                  },
                  type: ActionTypes.EDIT,
                  actionName: 'edit-vppa',
                  tooltip: 'Edit',
                };
                const deleteBtn = {
                  name: deleteIcon(),
                  callback: (detail) => {
                    if (detail) {
                      context.$emit('deleteVppa', detail);
                    }
                  },
                  type: ActionTypes.DELETE,
                  actionName: 'delete-vppa',
                  tooltip: 'Delete',
                };

                actions = [filterBtn, editBtn, deleteBtn];
              }
            } else if (
              context.actualCompanyId !== params.data.VPPA_COMPANY_ID &&
              context.authService.validateProfile(PROFILE_RIGHTS.UPSERT_NEGOTIATION)
            ) {
              const documentBtn = {
                name: 'Create Proposal',
                callback: (detail) => {
                  context.$emit('createProposal', detail);
                },
                type: ActionTypes.CREATE,
                actionName: 'confirm-trade',
              };
              actions = [documentBtn];
            }
            return {
              actions,
              enabled: true,
            };
          },
        },
      ],
    },
  ];
}
